<template>
  <div class="cs-publish">
    <div v-if="!definitionId" class="cs-publish__state">
      <v-icon size="28" color="primary">mdi-content-save-outline</v-icon>
      <div class="cs-publish__state-title">{{ copy.NoDefinitionTitle }}</div>
      <div class="cs-publish__muted">{{ copy.NoDefinitionMessage }}</div>
    </div>

    <template v-else>
      <!-- Flat summary: every decision the wizard collected, on one card, in the order they
           were made. Deliberately not a re-editable form — this step's job is to be read. -->
      <section class="cs-publish__card">
        <div class="cs-publish__card-title">{{ copy.PublishSummaryTitle }}</div>

        <dl class="cs-publish__summary">
          <template v-for="row in summaryRows">
            <dt :key="`${row.label}-t`">{{ row.label }}</dt>
            <dd :key="`${row.label}-d`">
              <span :class="{ 'cs-publish__muted': row.muted }">{{ row.value }}</span>
              <span v-if="row.note" class="cs-publish__note">{{ row.note }}</span>
            </dd>
          </template>
        </dl>
      </section>

      <section
        v-if="outcome === 'published'"
        class="cs-publish__card cs-publish__card--success"
        role="status"
        aria-live="polite">
        <div class="cs-publish__success-head">
          <v-icon size="22" color="success">mdi-check-circle-outline</v-icon>
          <span class="cs-publish__card-title">{{ copy.PublishSuccessTitle }}</span>
        </div>
        <div v-if="compiledViewName" class="cs-publish__viewname">{{ compiledViewName }}</div>
        <div class="cs-publish__muted">{{ copy.PublishSuccessHint }}</div>
      </section>

      <section v-else-if="isPublished" class="cs-publish__card">
        <div class="cs-publish__card-title">{{ copy.PublishedStateTitle }}</div>
        <div v-if="compiledViewName" class="cs-publish__viewname">{{ compiledViewName }}</div>
        <div class="cs-publish__muted">{{ copy.PublishedStateMessage }}</div>
      </section>

      <section v-else-if="isBroken" class="cs-publish__card cs-publish__card--error" role="status">
        <div class="cs-publish__success-head">
          <v-icon size="22" color="error">mdi-alert-circle-outline</v-icon>
          <span class="cs-publish__card-title">{{ copy.BrokenStateTitle }}</span>
        </div>
        <div class="cs-publish__muted">{{ copy.BrokenStateMessage }}</div>
      </section>

      <section v-if="failure" class="cs-publish__card cs-publish__card--error" role="alert">
        <div class="cs-publish__success-head">
          <v-icon size="22" color="error">mdi-alert-circle-outline</v-icon>
          <span class="cs-publish__card-title">{{ failure.title }}</span>
        </div>
        <div v-if="failure.issues.length" class="cs-publish__issues">
          <div v-for="(issue, i) in failure.issues" :key="i" class="cs-publish__issue">
            <span v-if="issue.step" class="cs-publish__issue-step">{{ issue.step }}</span>
            <span>{{ issue.message }}</span>
          </div>
        </div>
        <div v-else class="cs-publish__muted">{{ failure.message }}</div>
      </section>

      <!-- The primary action lives in the step body, not the wizard footer: the footer's Next is
           hidden on the last step, and the footer belongs to the wizard. -->
      <div v-if="isDraft" class="cs-publish__actions">
        <v-btn
          color="primary"
          depressed
          :disabled="busy || hasUnsavedChanges || !previewVerified"
          :loading="publishing"
          @click="publish">
          <v-icon left size="18">mdi-publish</v-icon>
          {{ copy.PublishAction }}
        </v-btn>
        <span v-if="hasUnsavedChanges" class="cs-publish__muted">
          {{ copy.PublishSaveFirstMessage }}
        </span>
        <span v-else-if="!previewVerified" class="cs-publish__muted">
          {{ copy.PublishPreviewFirstMessage }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
  import { log, error } from '@/utils/debugLogger';
  import compositeSourceService from '../../service/compositeSourceService';
  import { CompositeSourceStatus } from '../../config/compositeSourceEnums';
  import { RootNodeId } from '../../model/definitionVocabulary';
  import { describeStrategyChip, humanizeWireWord } from '../relations/relationsCopy';
  import { PreviewPublishCopy, validationIssues, describeFailure } from './previewPublishCopy';

  /**
   * Step 5 — read the definition back as one flat summary, then publish it.
   *
   * A Draft publishes only after its local edits have been explicitly saved. Published and Broken
   * candidates use the wizard footer's Apply changes / Repair source actions, which send the local
   * JSON atomically instead of cycling through unpublish.
   */
  export default {
    name: 'StepPublish',

    props: {
      /** definitionModel for this authoring session. */
      model: { type: Object, required: true },
      /** Authoring graph for the local root, or null while it is being resolved. */
      graph: { type: Object, default: null },
      /** Selected workspace target: {kind, id, name}. */
      target: { type: Object, required: true },
      /** The saved definition's id. Not part of the uniform step trio — see StepPreview. */
      definitionId: { type: String, default: null },
      /** The saved row's concurrency token, sent as If-Match. */
      rowVersion: { type: String, default: null },
      /** The saved row's status — `Draft`, `Published` or `Broken`. */
      status: { type: String, default: CompositeSourceStatus.Draft },
      /** The view this definition compiled to, when it has one. */
      viewName: { type: String, default: null },
      /** Whether the local model differs from the last successful write. */
      hasUnsavedChanges: { type: Boolean, default: false },
      /** Whether Preview successfully ran this exact local name + definition snapshot. */
      previewVerified: { type: Boolean, default: false },
    },

    data() {
      return {
        copy: PreviewPublishCopy,
        currentStatus: this.status,
        currentRowVersion: this.rowVersion,
        currentViewName: this.viewName,
        outcome: null,
        failure: null,
        publishing: false,
      };
    },

    computed: {
      isPublished() {
        return this.currentStatus === CompositeSourceStatus.Published;
      },

      isDraft() {
        return this.currentStatus === CompositeSourceStatus.Draft;
      },

      isBroken() {
        return this.currentStatus === CompositeSourceStatus.Broken;
      },

      busy() {
        return this.publishing;
      },

      compiledViewName() {
        return this.currentViewName;
      },

      /** Relations that produce a column rather than a join — a count or an exists. */
      scalarNodes() {
        return this.model.nodes().filter((node) => node.isScalar);
      },

      rootLabel() {
        const root = this.model.nodes().find((node) => node.id === RootNodeId);
        if (!root) return null;
        const kind = humanizeWireWord(root.type);
        return kind && root.label !== kind ? `${root.label} (${kind})` : root.label;
      },

      tagDesignationLabel() {
        const nodeId = this.model.state.tagColumnSource;
        if (!nodeId) return null;
        const node = this.model.nodes().find((candidate) => candidate.id === nodeId);
        return (node && node.label) || nodeId;
      },

      relationsSummary() {
        const edges = this.model.state.edges || [];
        if (!edges.length) return { value: 'None', muted: true, note: null };

        const words = edges
          .map((edge) => describeStrategyChip(edge.strategy))
          .filter(Boolean)
          .join(', ');

        return {
          value: `${edges.length} ${edges.length === 1 ? 'Relation' : 'Relations'}`,
          muted: false,
          note: words || null,
        };
      },

      /**
       * The authored projection. Scalar relations are counted with the columns because that is
       * what they are — a count relation IS a column — and leaving them out would under-report
       * the width of the result against what the preview just showed.
       */
      columnsSummary() {
        const picked = (this.model.state.columns || []).length;
        const scalars = this.scalarNodes.length;
        const total = picked + scalars;

        return {
          value: `${total} ${total === 1 ? 'Column' : 'Columns'}`,
          muted: total === 0,
          note: scalars
            ? `${picked} Picked, ${scalars} From count or exists relations`
            : null,
        };
      },

      summaryRows() {
        const relations = this.relationsSummary;
        const columns = this.columnsSummary;
        const tag = this.tagDesignationLabel;

        return [
          { label: 'Name', value: this.model.state.name || 'Unnamed', muted: !this.model.state.name, note: null },
          { label: 'Target', value: this.target.name, muted: false, note: null },
          { label: 'Built from', value: this.rootLabel || 'Not chosen', muted: !this.rootLabel, note: null },
          { label: 'Relations', value: relations.value, muted: relations.muted, note: relations.note },
          { label: 'Columns', value: columns.value, muted: columns.muted, note: columns.note },
          {
            label: 'Tag designation',
            value: tag || this.copy.PublishNoTagDesignation,
            muted: !tag,
            note: null,
          },
        ];
      },
    },

    watch: {
      // The wizard keeps saving while this step is open (its Save draft button never leaves the
      // footer), so a fresh row version from up there replaces ours.
      rowVersion(value) {
        if (value) this.currentRowVersion = value;
      },
      status(value) {
        this.currentStatus = value;
      },
      viewName(value) {
        this.currentViewName = value;
      },
    },

    methods: {
      async publish() {
        if (!this.definitionId || !this.target || !this.isDraft
          || this.hasUnsavedChanges || !this.previewVerified || this.busy) return;

        this.setPublishing(true);
        this.failure = null;
        this.outcome = null;

        log('[StepPublish] publish requested', {
          definitionId: this.definitionId,
          targetId: this.target.id,
          fromStatus: this.currentStatus,
        });

        try {
          const res = await compositeSourceService.publish(
            this.definitionId,
            this.target.id,
            this.currentRowVersion,
          );
          this.absorb(res && res.data);
          this.outcome = 'published';

          log('[StepPublish] published', {
            definitionId: this.definitionId,
            status: this.currentStatus,
            compiledViewName: this.currentViewName,
          });
        } catch (err) {
          this.reportFailure(err, this.copy.PublishFailedFallback);
        } finally {
          this.setPublishing(false);
        }
      },

      setPublishing(busy) {
        this.publishing = !!busy;
        this.$emit('busy-change', this.publishing);
      },

      /**
       * Takes the server's answer as the new truth and passes it up, so status, row version and
       * view name move together.
       */
      absorb(definition) {
        if (!definition) return;

        this.currentStatus = definition.status || this.currentStatus;
        this.currentRowVersion = definition.rowVersion || this.currentRowVersion;
        this.currentViewName = definition.compiledViewName || null;

        this.$emit('definition-updated', definition);
      },

      /**
       * A refusal, rendered where the author is looking rather than as a toast that scrolls away.
       * Validator complaints keep their per-rule detail and name the step that owns each one.
       */
      reportFailure(err, fallback) {
        error('[StepPublish] action failed', { definitionId: this.definitionId, err });

        this.failure = {
          title: fallback,
          issues: validationIssues(err),
          message: describeFailure(err, fallback),
        };
      },
    },
  };
</script>

<style scoped>
  .cs-publish__card {
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-md, 10px);
    padding: 14px 16px;
    margin-bottom: 16px;
  }

  .cs-publish__card--success {
    border-color: var(--v-success-base);
  }

  .cs-publish__card--error {
    border-color: var(--v-error-base);
  }

  .cs-publish__card-title {
    font-family: var(--ds-font-family-heading);
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .cs-publish__success-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .cs-publish__summary {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 8px 16px;
    margin: 12px 0 0;
  }

  .cs-publish__summary dt {
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-publish__summary dd {
    margin: 0;
    font-size: 0.875rem;
    min-width: 0;
  }

  .cs-publish__note {
    display: block;
    font-size: 0.75rem;
    color: var(--v-text-base);
    margin-top: 2px;
  }

  .cs-publish__viewname {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.875rem;
    margin: 4px 0 6px;
    word-break: break-all;
  }

  .cs-publish__muted {
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-publish__issues {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
  }

  .cs-publish__issue {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-publish__issue-step {
    flex-shrink: 0;
    font-weight: 600;
    color: var(--v-error-base);
  }

  .cs-publish__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cs-publish__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
    padding: 48px 24px;
  }

  .cs-publish__state-title {
    font-family: var(--ds-font-family-heading);
    font-size: 1rem;
    font-weight: 600;
  }

</style>
